import * as React from "react";
import { formSet, useCpFormAnyValueId } from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import {
  SingleNavMenuLink,
  toLinkButton,
  toLinkCategory,
  toLinkCategoryEnd,
  toLinkPage,
  toLinkSpace,
} from "../../identity/useNavMenu";
import { CloudAccessOverview } from "../cloudAccessOverview";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { General } from "./general";
import { Overview } from "./overview";
import { IdentityCreateCloudaccessAWS } from "./cloudaccess/aws";
import { IdentityCreateCloudaccessAzure } from "./cloudaccess/azure";
import { IdentityCreateCloudaccessGCP } from "./cloudaccess/gcp";
import { IdentityCreateCloudaccessNGS } from "./cloudaccess/ngs";
import { IdentityCreateNetworkResourceFQDN } from "../fqdnResource";
import { IdentityCreateNetworkResourceIP } from "../ipResource";
import { IdentityCreateNetworkResourceAWS } from "../awsResource";
import { IdentityCreateNetworkResourceGCP } from "../gcpResource";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const IdentityCreateContent: React.FC<Props> = ({ ignoreBlockerRef }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/identity/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);

  React.useEffect(() => {
    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Identity", "/general", { names: [`name`, `description`, `gvc`] }),
      toLinkCategory("Cloud Access"),
      toLinkPage("Overview", "/cloud-access", { names: ["aws", "azure", "gcp", "ngs"] }),
      toLinkPage("AWS", "/cloud-access-aws", { names: ["aws"] }),
      toLinkPage("Azure", "/cloud-access-azure", { names: ["azure"] }),
      toLinkPage("GCP", "/cloud-access-gcp", { names: ["gcp"] }),
      toLinkPage("Nats", "/cloud-access-ngs", { names: ["ngs"] }),
      toLinkCategoryEnd(),
      toLinkCategory("Cloud Wormhole"),
      toLinkPage("FQDN Resources", "/cloud-wormhole-fqdn"),
      toLinkPage("IP Resources", "/cloud-wormhole-ip"),
      toLinkCategoryEnd(),
      toLinkCategory("Native Networking"),
      toLinkPage("AWS Private Link", "/native-networking-aws"),
      toLinkPage("GCP Service Connect", "/native-networking-gcp"),
      toLinkCategoryEnd(),
      toLinkPage("Tags", "/tags", { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        disabled: submitting,
        danger: true,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => formSet(formId, `meta.previewing`, true),
        disabled: submitting,
      }),
      toLinkButton("Create", {
        type: "primary",
        onClick: async () => {
          await submit();
        },
        loading: submitting,
        disabled: submitting,
        className: "action",
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
        }),
      );
    }

    return links;
  }, [submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route
                path={`general/*`}
                element={
                  <div>
                    <div className="mb-6">
                      <Overview />
                    </div>
                    <General />
                  </div>
                }
              />
              <Route path={`cloud-access`} element={<CloudAccessOverview />} />
              <Route path={`cloud-access-aws/*`} element={<IdentityCreateCloudaccessAWS />} />
              <Route path={`cloud-access-azure/*`} element={<IdentityCreateCloudaccessAzure />} />
              <Route path={`cloud-access-gcp/*`} element={<IdentityCreateCloudaccessGCP />} />
              <Route path={`cloud-access-ngs/*`} element={<IdentityCreateCloudaccessNGS />} />
              <Route path={`cloud-wormhole-fqdn/*`} element={<IdentityCreateNetworkResourceFQDN />} />
              <Route path={`cloud-wormhole-ip/*`} element={<IdentityCreateNetworkResourceIP />} />
              <Route path={`native-networking-aws/*`} element={<IdentityCreateNetworkResourceAWS />} />
              <Route path={`native-networking-gcp/*`} element={<IdentityCreateNetworkResourceGCP />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
